import { useCallback, useRef, useState } from "react";
import { useLocation } from "wouter";
import { FileText, Loader2, Upload, X, ClipboardPaste } from "lucide-react";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { Input, Textarea } from "@/components/ui/Inputs";
import { useCreateProject } from "@/hooks/useProjects";
import { useFileUploadList } from "@/hooks/useFileUploadList";
import { cn } from "@/lib/utils";

type SourceMode = "paste" | "upload";

export default function NewRFP() {
  const [, navigate] = useLocation();

  const [mode, setMode] = useState<SourceMode>("upload");
  const [title, setTitle] = useState("");
  const [requesterName, setRequesterName] = useState("");
  const [deadline, setDeadline] = useState("");

  // Paste-text path
  const [rfpText, setRfpText] = useState("");

  // Upload path
  const {
    items,
    addFiles,
    removeItem,
    reset: resetUpload,
    allFinishedSuccessfully,
  } = useFileUploadList({
    kind: "rfp_source",
  });

  const create = useCreateProject();

  const canSubmit =
    title.trim().length > 0 &&
    requesterName.trim().length > 0 &&
    (mode === "paste" ? rfpText.trim().length > 0 : allFinishedSuccessfully);

  const handleCreate = async () => {
    if (!canSubmit) return;
    const fileIds = items.filter((item) => item.fileId).map((item) => item.fileId!);
    const project = await create.mutateAsync({
      title,
      requesterName,
      submissionDeadline: deadline ? new Date(deadline).toISOString() : null,
      ...(mode === "paste" ? { rawText: rfpText } : { rfpFileIds: fileIds }),
    });
    navigate(`/projects/${project.id}/summary`);
  };

  return (
    <Layout>
      <PageHeader
        title="New RFP Project"
        subtitle="Upload the RFP file (PDF or Word) — the AI reads it and pulls out the key details. You can paste raw text instead if you don't have the file."
      />

      <div className="p-8 max-w-3xl space-y-6">
        <Card>
          <div className="space-y-4">
            <Input
              label="Project Title"
              placeholder="e.g. CDFA — Integrated Marketing Communications RFP"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Input
              label="Requester (organization issuing the RFP)"
              placeholder="e.g. Caribbean Development Finance Authority"
              value={requesterName}
              onChange={(e) => setRequesterName(e.target.value)}
            />
            <Input
              label="Submission Deadline (optional)"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />

            {/* Source mode toggle */}
            <div>
              <label className="text-xs font-medium text-gray-400 mb-2 block">RFP Source</label>
              <div className="flex gap-2">
                <ModeButton
                  active={mode === "upload"}
                  onClick={() => setMode("upload")}
                  icon={<Upload size={14} />}
                  label="Upload PDF / Word"
                />
                <ModeButton
                  active={mode === "paste"}
                  onClick={() => setMode("paste")}
                  icon={<ClipboardPaste size={14} />}
                  label="Paste text instead"
                />
              </div>
            </div>

            {mode === "paste" ? (
              <Textarea
                label="RFP Text"
                placeholder="Paste the full RFP text here. AI parsing extracts the summary and matrix once the project is created."
                rows={14}
                value={rfpText}
                onChange={(e) => setRfpText(e.target.value)}
              />
            ) : (
              <MultiFileUploadZone
                items={items}
                onAddFiles={addFiles}
                onRemoveItem={removeItem}
                onClear={resetUpload}
              />
            )}
          </div>

          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs" style={{ color: "#6B7280" }}>
              <FileText size={12} />
              {mode === "paste"
                ? `${rfpText.trim().split(/\s+/).filter(Boolean).length} words`
                : items.length > 0
                  ? `${items.length} file${items.length === 1 ? "" : "s"} selected`
                  : "No files selected"}
            </div>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!canSubmit || create.isPending}
              icon={create.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
            >
              {create.isPending ? "Creating…" : "Create Project"}
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------

interface ModeButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

function ModeButton({ active, onClick, icon, label }: ModeButtonProps) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all border",
      )}
      style={
        active
          ? {
              backgroundColor: "rgba(249,115,22,0.15)",
              color: "#F97316",
              borderColor: "rgba(249,115,22,0.3)",
            }
          : {
              backgroundColor: "rgba(255,255,255,0.05)",
              color: "#9CA3AF",
              borderColor: "rgba(229,231,235,0.08)",
            }
      }
    >
      {icon}
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------

interface MultiFileUploadZoneProps {
  items: ReturnType<typeof useFileUploadList>["items"];
  onAddFiles: (files: File[]) => void;
  onRemoveItem: (tempId: string) => void;
  onClear: () => void;
}

function MultiFileUploadZone({
  items,
  onAddFiles,
  onRemoveItem,
  onClear,
}: MultiFileUploadZoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      onAddFiles(Array.from(files));
    },
    [onAddFiles],
  );

  const isUploading = items.some((item) => item.status === "uploading" || item.status === "idle");

  return (
    <div className="flex flex-col gap-4">
      <label className="text-xs font-medium text-gray-400">RFP Files</label>

      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => !isUploading && inputRef.current?.click()}
        className={cn(
          "rounded-xl border-2 border-dashed px-6 py-10 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all",
          isUploading ? "cursor-default opacity-60" : "",
        )}
        style={{
          borderColor: dragOver ? "rgba(249,115,22,0.5)" : "rgba(229,231,235,0.15)",
          backgroundColor: dragOver ? "rgba(249,115,22,0.05)" : "#111318",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          multiple
        />

        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
        >
          <Upload size={20} style={{ color: "#F97316" }} />
        </div>
        <div className="text-center">
          <p className="text-sm text-white font-medium">
            Drop PDFs or DOCX files here, or click to browse
          </p>
          <p className="mt-1 text-xs" style={{ color: "#6B7280" }}>
            Max 10 MB each · uploads directly to your secure cloud storage
          </p>
        </div>
      </div>

      {/* File list */}
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.tempId}
              className="flex items-center gap-3 p-3 rounded-lg"
              style={{ backgroundColor: "rgba(255,255,255,0.02)" }}
            >
              <div>
                {item.status === "done" && <FileText size={16} style={{ color: "#22c55e" }} />}
                {item.status === "uploading" && (
                  <Loader2 size={16} className="animate-spin" style={{ color: "#F97316" }} />
                )}
                {item.status === "error" && <FileText size={16} style={{ color: "#EF4444" }} />}
                {item.status === "idle" && <FileText size={16} style={{ color: "#9CA3AF" }} />}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{item.filename}</p>
                <p className="text-xs" style={{ color: "#6B7280" }}>
                  {(item.byteSize / 1024 / 1024).toFixed(1)} MB
                </p>
                {item.status === "uploading" && (
                  <div
                    className="mt-1.5 h-1 rounded-full overflow-hidden"
                    style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
                  >
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${item.progressPct}%`,
                        backgroundColor: "#F97316",
                      }}
                    />
                  </div>
                )}
                {item.error && <p className="text-xs text-red-400 mt-1">{item.error}</p>}
              </div>

              {item.status === "done" && (
                <span
                  className="text-xs px-2 py-1 rounded-full font-medium"
                  style={{ backgroundColor: "rgba(34,197,94,0.1)", color: "#22c55e" }}
                >
                  Ready
                </span>
              )}

              <button
                type="button"
                onClick={() => onRemoveItem(item.tempId)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-white/5 flex-shrink-0"
                title="Remove file"
                aria-label="Remove file"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add another file link */}
      {items.length > 0 && !isUploading && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-xs text-orange-400 hover:text-orange-300 font-medium"
        >
          Add another file
        </button>
      )}
    </div>
  );
}
